/**
 * Canonical list of the flagship apps showcased on the welcome splash.
 *
 * Plain module — NO db / server-only imports — so it can be shared by the
 * `/?app=<id>` funnel resolver and client components alike.
 *
 *   • `appId`      — the splash app-card id; the funnel is `/?app=<appId>`,
 *                    which resolves the app's `@robutler`-authored widget and
 *                    deep-links to its `/w/<contentId>` standalone viewer
 *                    (`resolveFlagshipWidgetContentId`).
 *   • `widgetType` — the stable key on the seeded widget `content` row
 *                    (`metadata->>'widgetType'`, set by seed-system-widgets).
 *   • `title`      — display name.
 *
 * THE SIGN-IN SCREEN CONSTANTS (2026-10-02, app-sign-in-and-onboarding.md
 * sections 4.1, 4.5 and decision 6). An app may narrow and reorder the
 * platform's sign-in providers and put its own heading, line and two square
 * images on the screen a person signs in to it from. The owner-written
 * setting (`signInProviders` from the bundle declaration, `signInScreen` on
 * the catalog row) is the week after Tech Week; for Monday both are
 * CONSTANTS here, on the entry the client already resolves the funnel's
 * screen against (lib/auth/login-app.ts), so a link can only ever name a
 * flagship id and nothing can be forged onto the screen. Heading and line
 * are TRANSLATION KEYS rather than literal text, because the screen is
 * drawn in fifteen languages; the owner-written setting will carry plain
 * text, as section 4.1 specifies, and the client will prefer it when it
 * exists. The image paths are files under `public/images/sign-in/`: under
 * `/images`, which every host already passes through to the platform
 * (lib/domains/app-routing.ts), so a root binding on a host cannot swallow
 * the picture its own login page asks for, and no new top-level path is
 * claimed from a customer's domain for it (2026-10-05).
 *
 * THE PICTURE AND THE LINE BELONG TO THE PROJECT, NOT TO THE APP (the owner,
 * 2026-10-04: "make the login image defined by project - ie TECH WEEK
 * graphics + 'Your day at SF Tech Week, with your own agent to build and
 * explain it.' also project defined?"). Revent is one app and many
 * conferences, and each conference is a project folder: SF Tech Week is one,
 * a conference an organizer made is another. Until that day the screen had
 * one line and one picture per APP, and both were SF Tech Week's, so the
 * sentence named a week that an organizer's own link has nothing to do with
 * and the picture was a product shot that named nothing. Now an app's screen
 * may carry `projects`, keyed by the project's folder id (the id a conference
 * link carries, `#f=<folder id>`), each with its own line and picture, and
 * `defaultProject`, the one the bare `/?app=<id>` link opens. A sign-in for
 * a project this table does not know (somebody else's conference, a scanned
 * code that names none) keeps the app's own line and picture, so one
 * project's art is never drawn for another. `lib/auth/login-app.ts` works
 * out which project a sign-in is for.
 *
 * Still constants, for the reason above: the apex `/login` is static and may
 * draw only what ships with it, and a link must not be able to put a picture
 * or a sentence on the platform's sign-in screen. The owner-written setting
 * of section 4 is per project when it comes.
 *
 * THE HEADING IS THE PROJECT'S TOO, AND THE SCREENSHOTS ARE GONE (the owner,
 * 2026-10-04 night, on the wireframes for the Data Phoenix send). A project
 * may word its own heading (`headingKey`: SF Tech Week's is the email's
 * promise, "Get your superconnector agent"), and since such a heading no
 * longer says which app this is ("'Revent' app is not there? where are the
 * users signing in into? unclear"), the screen then draws the app's name
 * under its mark. A project may also name who SENT a person (`partners`):
 * the campaign's source tag picks one name out of that fixed table, and the
 * screen says "<Partner> x Robutler" under the heading; the tag is a key and
 * never text. The app's own picture went with the same note ("why the
 * screenshot? for code show two avatars (those who sent you and you) and a
 * connection line + just Revent wordmark for generic login where hero is not
 * set?"): it was a stand-in product shot, soft on a 2x screen, with a mouse
 * pointer in it. An app with a sign-in screen and no picture now gets its
 * COLOUR alone in the band, and a scanned code gets two people and a line,
 * drawn by components/auth/login-dialog-body.tsx from nothing but the app's
 * colour and the inviter's name the screen already draws. (The app's name
 * stood on the colour for a few hours; the owner, 2026-10-05: "in hero, event
 * name instead of 'Revent'? or nothing, just grad if no name available?". An
 * event's name is the wanted hero and waits for a lookup by the event's id:
 * the link carries no title and must not.)
 *
 * LA TECH WEEK'S SCREEN IS THE DEFAULT, AND ITS KEY IS ITS CONFERENCE'S
 * FOLDER (the owner, 2026-10-10: "update the landing screen", with two mock
 * pictures of the bare link; and the same night, of the picture: "why should
 * it be committed? is not it configured per conference?"). LA Tech Week runs
 * the week after SF Tech Week (October 12 to 18). For one day its screen was
 * keyed by a NAME, because the app had no calendar for it and a folder id
 * was not to be invented. On 2026-10-11 LA Tech Week was published as a
 * conference project, with no code of its own in the app, on ids derived
 * from its edition id (`la-2026`) by one pure function
 * (public/widgets/revent/lib/edition-ids.js `conferenceIds`: the publisher
 * writes on them, the app accepts a conference only on them, and this file
 * computes the same). So `REVENT_LA_2026_PROJECT` is that folder id now,
 * computed, never a second copy of a literal: a link that names the LA
 * conference (`#f=<its folder>`, or `?conference=la-2026`,
 * lib/auth/arrival-query.ts) draws the LA screen exactly as the bare link
 * does, and SF Tech Week's entry keeps drawing for the links that name its
 * folder. The two entries word the same promise and line; what differs is
 * the band. LA's band is three lines of condensed
 * type on the desktop and two on a phone, drawn as PICTURES by
 * scripts/sign-in-band-art.ts from the words, in the face the owner named
 * (Pathway Gothic One), because the apex /login is static and loads no font
 * for one week's three lines; the two files hold different art, so each
 * carries its own `artHeight`. The screen's mark is the mock's, a connector
 * of joined points on pink, and it is the SIGN-IN SCREEN's alone: the app's
 * tile, host theme and catalog row keep the rose calendar of the identity
 * table (lib/content/app-identity.ts), and the mark constant below is read by
 * components/auth/login-dialog-body.tsx and by nothing else.
 */
import type { SignInProvider } from '../auth/sign-in-providers';
/* The derivation of a published conference's ids. A plain file with no
   imports, read by its relative path (never `@/`: this module is in the
   agent-runtime graph, scripts/check-agent-runtime-imports.ts), and the same
   file the Revent bundle and the catalog publisher import. */
import { conferenceIds } from '../../public/widgets/revent/lib/edition-ids.js';

/** The pictures of one sign-in screen: an app's own, or one project's. */
export type FlagshipSignInImage = {
  phone: string;
  desktop: string;
  /** Art made to cover the whole area (a photo, an illustration): edge to edge, centred, cropped to fit. */
  fill?: boolean;
  /**
   * Art that must be seen WHOLE and at its own shape: a wordmark, a lockup
   * (2026-10-04, the SF Tech Week project's picture). A product shot's window
   * and `fill` both crop, and a wordmark with a letter cut off is wrong. It
   * is drawn centred, never cropped and never wider than `maxPx` CSS pixels
   * (so an 800 px file stays sharp on a 2x screen), on `ground`, which must
   * be the art's own background colour so its edges do not show. The app's
   * colour and its highlight are not drawn behind it.
   */
  whole?: {
    ground: string;
    maxPx: number;
    /**
     * How much of the file's HEIGHT the art itself covers, centred, as a
     * fraction (2026-10-04 night). The file must be SQUARE. A wordmark file
     * carries its own margin, and at phone width the band above the form is
     * short: fitting the FILE into it spent half the height on the file's
     * black margin, and the SF Tech Week wordmark came out 76 px wide on an
     * iPhone with the browser's bars showing (it had only ever been looked at
     * in an 812 px tall window). With this set the file is sized so the ART
     * fits the band's height, and still never wider than the band or than
     * `maxPx`; its side margin is kept as breathing room. A test measures the
     * file and holds this number to it.
     *
     * ONE SHARE PER FILE where the two files hold different art (2026-10-10,
     * LA Tech Week's band: two lines across the phone file's middle, three in
     * a taller block on the desktop file). One number was not true of both:
     * the phone's share on the desktop file clips the block in a short
     * window, the desktop's on the phone file drew the two lines at half
     * their size. The desktop's share applies from `md` up, where the desktop
     * file is served; a single number still applies to both.
     */
    artHeight?: number | { phone: number; desktop: number };
  };
};

/**
 * THE LINK'S OWN PREVIEW CARD (2026-10-12, the production two-account test).
 * A shared `/?app=revent` unfurled in Discord, Slack, iMessage, LinkedIn and
 * X as the generic site card: "Robutler · Pro-grade collaborative apps", the
 * site line and the yellow mark, because a signed-out visitor (and every
 * link-preview crawler) is answered 307 to `/login?signup=1&returnTo=...`
 * and the apex `/login` is static and carries the root layout's card. The
 * card of an app link is now built from the project's hero, the same words
 * and colours the sign-in band wears: this picture (a 1200x630 PNG, the one
 * shape every crawler accepts; webp is not) drawn by
 * scripts/sign-in-band-art.ts from the band's own lines, with `alt` being
 * those words. The title and the line of the card are the project's heading
 * and line keys below, in English; nothing in a link is ever drawn on it
 * (lib/auth/app-link-card.ts). Served by `app/(auth)/login/app/page.tsx`,
 * the dynamic sibling the proxy rewrites an app link's sign-in to, so the
 * static apex `/login` stays static.
 */
export type FlagshipLinkCard = {
  /** Under `public/`, absolute path; a PNG or a JPEG, 1200x630. */
  image: string;
  width: 1200;
  height: 630;
  /** The words on the picture. */
  alt: string;
};

/** What ONE PROJECT of an app puts on the sign-in screen. See the module header. */
export type FlagshipProjectScreen = {
  /**
   * Translation key of the heading, when the project words its own. It is
   * drawn exactly where the project's line is (never over an invitation or a
   * sent link, whose lines say something else), and the app's name is then
   * drawn under its mark. Absent: the app's heading.
   */
  headingKey?: string;
  /**
   * Translation key of the app's label under its mark, drawn with the
   * project's own heading; `{app}` is the app's title. A project may say more
   * than the name there: SF Tech Week's says the app is free (the owner,
   * 2026-10-05: "Free Revent App?", in place of a third row "Free to use"
   * under the heading). Absent: `auth.appNameLabel`, "<App> App".
   */
  nameKey?: string;
  /**
   * Translation key of the line under the heading. A line break written in
   * the text is kept, so a line of two or three short sentences can put each
   * on its own row.
   */
  lineKey: string;
  /** The project's picture. Null draws the app's colour alone. */
  image: FlagshipSignInImage | null;
  /**
   * The project's link-preview card (`FlagshipLinkCard`). Absent: a link
   * that names this project previews as the app's default project does, or
   * as the site when no project of the app has one.
   */
  card?: FlagshipLinkCard;
  /**
   * WHO SENT THE PERSON, by the campaign's source tag (`utm_source`, lower
   * case): the partner's name as it is drawn in "<Partner> x Robutler" under
   * the heading. A fixed table: `signInPartnerName` (lib/auth/login-app.ts)
   * looks the tag up as an own key and nothing a link carries is ever drawn.
   */
  partners?: Readonly<Record<string, string>>;
};

export type FlagshipSignInScreen = {
  /**
   * The app's mark on the panel: a lucide name the mark component
   * allowlists (`LUCIDE_MARKS`, components/workspace/panels/content-preview.tsx)
   * on the colour the band wears too. The host page gets the mark from the
   * verified row instead; this is for the apex funnel, which has no row to
   * read. It need not be the app's identity (Revent's is not since
   * 2026-10-10, see the module header): it is this screen's mark.
   *
   * `tile` IS FOR A MARK DRAWN FOR THE SCREEN, where the house tile is not
   * what was drawn (the house tile: the colour on the 135 degree cover
   * gradient, lib/marketplace/cover.ts, under a glyph half the tile wide).
   * `ground` is the tile's CSS background as written and `glyphPx` the
   * glyph's size on the full-bleed screen's 56 px tile. Without it the mark
   * is the house tile, as it was for every screen before 2026-10-10.
   */
  mark: { icon: string; color: string; tile?: { ground: string; glyphPx: 24 | 28 } };
  /** Translation key of the heading; `{app}` is the app's title. */
  headingKey: string;
  /**
   * Translation key of the one line under the heading, for a sign-in that is
   * for no project the screen knows. `{app}` is the app's title.
   */
  lineKey: string;
  /**
   * The picture on the full-bleed page (the owner, 2026-10-02: "do full bleed
   * full screen"), served from `public/`: the phone one in the band across the
   * top at phone width, the desktop one in the right half from `md` up. Both
   * sit on the app's colour (`mark.color`). By default each is a product shot,
   * drawn as a window whose top-left corner shows and whose other edges run
   * off the screen, cropped from its top-left corner, so any aspect ratio of
   * the screen keeps the shot's most telling corner in view. `fill: true` is
   * for art made to cover the whole area (a photo, an illustration): it is
   * drawn edge to edge, centred. Size them for a 2x screen: the desktop one
   * about 1600 px square, the phone one about 1000. The workspace dialog
   * draws the phone one small above the heading. Null draws the app's COLOUR
   * alone in the same place (see the module header), which is what Revent
   * has had since its stand-in screenshots went.
   */
  image: FlagshipSignInImage | null;
  /**
   * The screens of the app's PROJECTS, keyed by project folder id, or by a
   * name no link can carry for a screen that is no folder's (LA Tech Week
   * 2026; see the module header). A project named here replaces the line and
   * the picture above, may word its own heading and may name its partners;
   * the mark and the providers are the app's.
   */
  projects?: Readonly<Record<string, FlagshipProjectScreen>>;
  /** The project the bare `/?app=<id>` link opens: a key of `projects`. */
  defaultProject?: string;
};

/**
 * SF Tech Week 2026 as a project: the folder id Revent's own data file gives
 * the built-in week (`SF_2026_FOLDER_ID`, public/widgets/revent/data/sf-2026.js).
 * A test reads that file and holds the two equal.
 */
export const REVENT_SF_2026_PROJECT = 'da75c3e7-5f20-4260-9000-000000000001';

/**
 * LA Tech Week 2026 (October 12 to 18) as a project: the folder its edition
 * id derives, which is where the catalog publisher wrote the conference
 * (scripts/revent-catalog.ts `--edition la-2026`) and what a link to it
 * carries. Computed with the publisher's own function, so the three can
 * never disagree; tests/unit/auth/login-app.test.ts spells the id out once,
 * as a literal, so a change to the derivation is seen here too.
 */
export const REVENT_LA_2026_EDITION = 'la-2026';
const LA_2026_IDS = conferenceIds(REVENT_LA_2026_EDITION) as { folderId: string; metaId: string };
export const REVENT_LA_2026_PROJECT: string = LA_2026_IDS.folderId;
/** The body document of that folder (`catalog.meta.json`), for the event-name lookup. */
export const REVENT_LA_2026_META_ID: string = LA_2026_IDS.metaId;

export type FlagshipApp = {
  appId: string;
  widgetType: string;
  title: string;
  /**
   * Sign in (a real account, not a guest) before the app opens. For an app
   * whose point is the person's own agent and their own data across devices:
   * Revent's link in the SF Tech Week launch email is one click to "your
   * sidekick", and a guest cannot have one (tech-week plan wave 1.1,
   * 2026-09-18). Anonymous visitors and guests go to sign-up with this link
   * as the return address, and land in their workspace with the app open.
   */
  signIn?: boolean;
  /**
   * The app's choice among the platform's sign-in providers, in its order:
   * the first is the primary button, the first two are shown, the rest sit
   * behind "Other ways to sign in". Narrows and reorders only
   * (`signInProvidersFor`); a provider this environment has no credentials
   * for is skipped, so with LinkedIn unconfigured Revent's first button is
   * Google. Absent: the platform's order.
   */
  signInProviders?: readonly SignInProvider[];
  /** The screen's mark, heading, line and images. Absent: the generic app screen. */
  signInScreen?: FlagshipSignInScreen;
  /**
   * What the root of the app's NAMED HOST opens once the host is issued
   * (`revent.robutler.app`; decision 3 of app-sign-in-and-onboarding.md,
   * decided by the owner on 2026-10-05). `'app'`: the app itself, on the
   * catalog row, the way `/?app=<id>` opens it on the apex. Absent: the
   * default for the app's kind, which for an app whose catalog row owns
   * document formats (an editor app) is the app home, a list of the
   * person's documents with the app at `/new` (lib/domains/editor-apps.ts,
   * `servesAppHome`). Revent is one app and many conferences, and its host
   * is to open the app, not a list.
   */
  hostRoot?: 'app';
};

export const FLAGSHIP_APPS: FlagshipApp[] = [
  { appId: 'video-studio', widgetType: 'video-editor', title: 'Video studio' },
  { appId: 'vector-design', widgetType: 'vector-editor', title: 'Vector design' },
  { appId: 'docs', widgetType: 'docs', title: 'Docs' },
  { appId: 'whiteboard', widgetType: 'collab-board', title: 'Whiteboard' },
  { appId: 'guitarro', widgetType: 'guitar-tuner', title: 'Guitarro' },
  /* Not on the splash wall (that list is its own): `/?app=revent` is the entry
     link for the tech-week send. */
  {
    appId: 'revent',
    widgetType: 'revent',
    title: 'Revent',
    signIn: true,
    // The named host opens the app at `/`, not the conference list (the
    // owner, 2026-10-05: "revent.robutler.app should show an app like
    // webagentos.com").
    hostRoot: 'app',
    // LinkedIn first because Revent asked for it (the owner, 2026-10-02);
    // Discord is reachable under "Other ways to sign in".
    signInProviders: ['linkedin', 'google', 'x'],
    signInScreen: {
      // THE SIGN-IN SCREEN'S OWN MARK (the owner's LA Tech Week mock,
      // 2026-10-10): a connector of joined points on the mock's pink, which
      // the band wears too for a scanned code and for an event's name. The
      // app itself keeps the rose calendar the identity table gives it
      // (lib/content/app-identity.ts: glyph `calendar`, tone `rose`, which is
      // what the verified host row resolves): this constant is drawn by
      // components/auth/login-dialog-body.tsx and nowhere else, so the mark
      // here rebrands nothing, and a host page draws the row's mark as before.
      // THE TILE IS THE MOCK'S TOO, MEASURED, NOT THE HOUSE TILE: its ground
      // runs straight down from a light pink to a deep one (fitted to the
      // mock's own pixels, every row within 2 of 255 a channel) and its glyph
      // is 24 px on the 56 px tile. The house tile on `color` was the first
      // build: a hotter magenta on the diagonal under a 28 px glyph, which is
      // a different mark beside the picture the owner drew.
      mark: {
        icon: 'waypoints',
        color: '#f229a4',
        tile: { ground: 'linear-gradient(180deg, #ea5bbf, #c1046f)', glyphPx: 24 },
      },
      headingKey: 'auth.signInToApp',
      // THE APP'S OWN LINE, for a sign-in that is for no project named below
      // (a conference somebody else made, a scanned code), and NO picture of
      // its own: the screen draws the app's colour alone, and two people and
      // a line for a scanned code (2026-10-04 night; the two stand-in
      // screenshots of 2026-10-02 are gone, see the module header).
      lineKey: 'auth.appLineGeneric',
      image: null,
      // THE BARE LINK OPENS THE DEFAULT SCREEN, on LA Tech Week's key (the
      // owner, 2026-10-10). The week ended on October 18, so since 2026-11-02
      // the entry draws words of no week; see that entry. SF Tech Week's entry
      // stays for the links that name its folder.
      defaultProject: REVENT_LA_2026_PROJECT,
      projects: {
        // SF TECH WEEK'S (the owner, 2026-10-04). Its picture is the "TECH /
        // WEEK" wordmark, white on black: the file is byte for byte the one
        // Revent ships for a Tech Week event with no picture of its own
        // (public/widgets/revent/lib/tech-week-art.js, where its provenance
        // and the reason it may be copied are written down; a test holds the
        // two files equal). It names the week the person is signing in for,
        // which is the nominative use that file describes, and like there it
        // is drawn for this one project and never for a conference a person
        // made.
        [REVENT_SF_2026_PROJECT]: {
          // The words of the Data Phoenix send (the owner, 2026-10-04 night):
          // the email's button is "Get your agent" and its second idea is
          // "Everyone gets a superconnector", so the screen a reader lands on
          // makes the same promise, and the line under it says what the
          // agent does, with you and without you. LA's entry words the same.
          headingKey: 'auth.appHeadingRevent',
          // "Free Revent App": that the APP is free is said once, here, which
          // is also the narrower and truer claim (the agent's answers draw on
          // an allowance; the app itself costs nothing).
          nameKey: 'auth.appNameRevent',
          lineKey: 'auth.appLineRevent',
          image: {
            phone: '/images/sign-in/revent-sf-2026.webp',
            desktop: '/images/sign-in/revent-sf-2026.webp',
            // The wordmark covers the middle half of the 800 px file's height
            // (402 of 800 px, measured; tests/unit/auth/login-body-app-screen.test.tsx).
            whole: { ground: '#000000', maxPx: 400, artHeight: 0.5 },
          },
          // The link's card: the same wordmark, centred on a 1200x630 black
          // card by scripts/sign-in-band-art.ts (the `card` jobs).
          card: { image: '/images/sign-in/revent-sf-2026-card.png', width: 1200, height: 630, alt: 'Tech Week' },
        },
        // THE DEFAULT SCREEN, ON LA TECH WEEK'S KEY. The owner, 2026-11-02: "LA
        // Tech Week -> Many events \n one you". The week ended and the bare
        // link went on selling it, so the picture now names no week, drawn by
        // scripts/sign-in-band-art.ts. The key stays: it is what every default
        // reader and a link to the finished conference already name.
        [REVENT_LA_2026_PROJECT]: {
          headingKey: 'auth.appHeadingRevent',
          nameKey: 'auth.appNameRevent',
          lineKey: 'auth.appLineRevent',
          image: {
            phone: '/images/sign-in/revent-default-phone.webp',
            desktop: '/images/sign-in/revent-default-desktop.webp',
            whole: { ground: '#000000', maxPx: 800, artHeight: { phone: 0.366, desktop: 0.387 } },
          },
          card: {
            image: '/images/sign-in/revent-default-card.png',
            width: 1200,
            height: 630,
            alt: 'Many events. One you.',
          },
          // The send's own tag (docs/internal/launch/revent-data-phoenix-campaign.md,
          // section 7: `utm_source=dataphoenix` on every link). The send's
          // links are the bare link, so the table is the default screen's.
          partners: { dataphoenix: 'Data Phoenix' },
        },
      },
    },
  },
];

export const FLAGSHIP_BY_APP_ID: Map<string, FlagshipApp> = new Map(
  FLAGSHIP_APPS.map((a) => [a.appId, a]),
);
